import { useMemo } from 'react';
import isEqual from 'react-fast-compare';
import { FilterToolbarItemId } from 'src/components/Filters';
import { useInternalNotesFilter } from 'src/components/Filters/InternalNotesFilter';
import { useMergedTagFilters } from 'src/components/MainFilterBar/Tags/useMergedTagFilters';
import { Content } from 'src/contexts/ContentContext';
import { useFilterQueryContext } from 'src/contexts/FilterQueryContext';
import { Checkbox } from 'src/core/interim/Checkbox';
import { DateRangeSelector } from 'src/core/POS/DateRangeSelector';
import { PosEnumMultiSelect } from 'src/core/POS/PosMultiSelect/PosEnumMultiSelect';
import { Slider } from 'src/core/ui/Slider';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ContentId } from 'src/utils/constants/contentId';
import {
  COLLECTION_FILTER_OP_TO_CID,
  PAYMENT_METHOD_TYPE_TO_CID,
  PURCHASE_PAYMENT_STATUS_TO_CID,
} from 'src/utils/constants/contentIdMaps';
import {
  StandardDateRangePresetNames,
  StandardDateRangePresetNamesV2,
} from 'src/utils/dateTimeUtils';
import { formatNumber } from 'src/utils/numberFormatter';
import {
  ActionOutboxEntityType,
  Feature,
  PurchaseOrderQueryV2,
  PurchaseOrderState,
  PurchaseQueryOperations,
} from 'src/WebApiController';

import { useDefaultYesNoFilterValues } from '../../../../components/Filters/hooks/useDefaultYesNoFilterValues';
import {
  CommonEventFiltersProps,
  useCommonEventFilters,
} from '../../../../components/MainFilterBar/AdvancedSearch/useCommonEventFilters';
import { useDeliveryTypeMultiFilters } from '../../../../components/MainFilterBar/AdvancedSearch/useDeliveryTypeMultiFilters';
import { usePurchaseInfoFilters } from '../../../../components/MainFilterBar/AdvancedSearch/usePurchaseInfoFilters';
import { usePurchaseIsConsignmentFilters } from '../../../../components/MainFilterBar/Filters/usePurchaseIsConsignmentFilters';
import {
  CurrencyFilterMultiSelector,
  CurrencyFilterSelector,
} from '../../../../components/Selectors/CurrencyFilterSelector';
import { PurchasePaymentMethodMultiSelector } from '../../../../components/Selectors/PurchasePaymentMethodSelector';

export const purchaseMandatoryFiltersToShow: FilterToolbarItemId[] = [
  'purchaseDates',
];

export function usePurchaseSearchFilters({
  isHorizontalLayout,
  showCommissionerFilter,
  showConsignmentFilter,
  showCatalogFilters,
  showVenueFilter,
  showPerformerFilter,
  firstRenderOpen,
  supportMultiOperation,
}: {
  isHorizontalLayout?: boolean;
  showCommissionerFilter?: boolean;
  showConsignmentFilter?: boolean;
  showCatalogFilters?: CommonEventFiltersProps<PurchaseOrderQueryV2>['showCatalogFilters'];
  showVenueFilter?: CommonEventFiltersProps<PurchaseOrderQueryV2>['showVenueFilter'];
  showPerformerFilter?: CommonEventFiltersProps<PurchaseOrderQueryV2>['showPerformerFilter'];
  firstRenderOpen?: boolean;
  supportMultiOperation?: boolean;
}) {
  const hasFilterByCurrencyCodeMultiFeature = useUserHasFeature(
    Feature.FilterByCurrencyCodeMulti
  );
  const hasFilterByCurrencyCodeFeature = !hasFilterByCurrencyCodeMultiFeature;
  const hasFilterByPaymentCurrencyCodeMultiFeature = useUserHasFeature(
    Feature.FilterByPaymentCurrencyCodeMulti
  );
  const hasDateFiltersRelativeExactCustomFeature = useUserHasFeature(
    Feature.DateFiltersRelativeExactCustom
  );

  const internalNotesFilter = useInternalNotesFilter<PurchaseOrderQueryV2>({
    entityType: ActionOutboxEntityType.Purchase,
    isHorizontalLayout,
  });

  const { initialQuery, tempQuery, setTempQuery } =
    useFilterQueryContext<PurchaseOrderQueryV2>();
  const { shouldEnableEmptySelection } = useDefaultYesNoFilterValues({});

  const isConsignmentFilter = usePurchaseIsConsignmentFilters({
    query: tempQuery,
    setQuery: setTempQuery,
    enableEmptySelection: shouldEnableEmptySelection('isConsignment'),
  });

  const purchaseInfoFilters = usePurchaseInfoFilters({
    query: tempQuery,
    setQuery: setTempQuery,
    reportType: undefined,
    useRelativePresets: false,
    showCommissionerFilter,
    supportMultiOperation,
    isHorizontalLayout,
  });

  const tagsFilter = useMergedTagFilters({
    query: tempQuery,
    setQuery: setTempQuery,
    entityType: ActionOutboxEntityType.Purchase,
    includeEventTags: false,
  });

  const eventFilter = useCommonEventFilters({
    initialQuery,
    query: tempQuery,
    setQuery: setTempQuery,
    showCatalogFilters,
    showVenueFilter,
    showPerformerFilter,
    firstRenderOpen,
    supportMultiOperation,
    isHorizontalLayout,
  });

  const deliveryTypeFilter = useDeliveryTypeMultiFilters({
    query: tempQuery,
    setQuery: setTempQuery,
    supportMultiOperation,
    isHorizontalLayout,
  });

  const filters = useMemo(() => {
    const costHandle: [number, number] = [
      tempQuery.totalCostLow || 0,
      tempQuery.totalCostHigh || 50000,
    ];

    return [
      {
        titleContentId: ContentId.WhenWhereWho,
        type: 'row',
        items: [
          ...purchaseInfoFilters.items,
          ...(showConsignmentFilter ? [isConsignmentFilter] : []),
        ],
      },
      {
        titleContentId: ContentId.Payments,
        type: 'row',
        items: [
          {
            filterId: 'paymentDates' as FilterToolbarItemId,
            labelContentId: ContentId.PaymentDate,
            filterQueryKeys: ['paymentDates'] as FilterToolbarItemId[],
            filterItem: (
              <DateRangeSelector
                useRelativePresets={
                  // This is a deprecated prop. Remove once DateFiltersRelativeExactCustom is GA'ed
                  hasDateFiltersRelativeExactCustomFeature ? false : true
                }
                presetNames={StandardDateRangePresetNames}
                exactPresetNames={
                  hasDateFiltersRelativeExactCustomFeature
                    ? StandardDateRangePresetNamesV2
                    : undefined
                }
                trailingPresetNames={
                  hasDateFiltersRelativeExactCustomFeature
                    ? StandardDateRangePresetNamesV2
                    : undefined
                }
                value={tempQuery.paymentDates}
                defaultValue={initialQuery.paymentDates}
                onBlur={({ value, operation }) =>
                  setTempQuery({
                    ...tempQuery,
                    paymentDates: value,
                    operations: supportMultiOperation
                      ? {
                          ...(tempQuery.operations as PurchaseQueryOperations),
                          paymentDatesOp: operation ?? null,
                        }
                      : null,
                  })
                }
                operation={tempQuery.operations?.paymentDatesOp ?? null}
                enableOperationSelect={supportMultiOperation}
                isHorizontalLayout={isHorizontalLayout}
              />
            ),
          },
          {
            filterId: 'paymentStatuses' as FilterToolbarItemId,
            labelContentId: ContentId.PaymentState,
            filterQueryKeys: ['paymentStatuses'] as FilterToolbarItemId[],
            filterItem: (
              <PosEnumMultiSelect
                style={{ width: '100%' }}
                values={tempQuery.paymentStatuses ?? []}
                placeholderText={ContentId.AllPaymentStates}
                enableEmptySelection
                operation={tempQuery.operations?.paymentStatusOp ?? null}
                operationOptionsContent={
                  supportMultiOperation
                    ? COLLECTION_FILTER_OP_TO_CID
                    : undefined
                }
                isHorizontalLayout={isHorizontalLayout}
                onChange={({ newValues, operation }) => {
                  if (
                    !isEqual(tempQuery.paymentStatuses, newValues) ||
                    operation !== tempQuery.operations?.paymentStatusOp
                  ) {
                    setTempQuery({
                      ...tempQuery,
                      paymentStatuses: newValues?.length ? newValues : null,
                      operations: supportMultiOperation
                        ? {
                            ...(tempQuery.operations as PurchaseQueryOperations),
                            paymentStatusOp: operation ?? null,
                          }
                        : null,
                    });
                  }
                }}
                valueOptionsContent={PURCHASE_PAYMENT_STATUS_TO_CID}
              />
            ),
          },
          {
            filterId: 'paymentMethods' as FilterToolbarItemId,
            labelContentId: ContentId.PaymentType,
            filterQueryKeys: ['paymentMethods'] as FilterToolbarItemId[],
            filterItem: (
              <PosEnumMultiSelect
                style={{ width: '100%' }}
                values={tempQuery.paymentMethods ?? []}
                placeholderText={ContentId.AllPaymentTypes}
                enableEmptySelection
                operation={tempQuery.operations?.paymentMethodOp ?? null}
                operationOptionsContent={
                  supportMultiOperation
                    ? COLLECTION_FILTER_OP_TO_CID
                    : undefined
                }
                isHorizontalLayout={isHorizontalLayout}
                onChange={({ newValues, operation }) => {
                  if (
                    !isEqual(tempQuery.paymentMethods, newValues) ||
                    operation !== tempQuery.operations?.paymentMethodOp
                  ) {
                    setTempQuery({
                      ...tempQuery,
                      paymentMethods: newValues?.length ? newValues : null,
                      operations: supportMultiOperation
                        ? {
                            ...(tempQuery.operations as PurchaseQueryOperations),
                            paymentMethodOp: operation ?? null,
                          }
                        : null,
                    });
                  }
                }}
                valueOptionsContent={PAYMENT_METHOD_TYPE_TO_CID}
              />
            ),
          },
          ...(hasFilterByPaymentCurrencyCodeMultiFeature
            ? [
                {
                  filterId: 'paymentCurrencyCodes' as FilterToolbarItemId,
                  labelContentId: ContentId.PaymentCurrency,
                  filterQueryKeys: [
                    'paymentCurrencyCodes',
                  ] as FilterToolbarItemId[],
                  filterItem: (
                    <CurrencyFilterMultiSelector
                      values={tempQuery.paymentCurrencyCodes ?? []}
                      triggerProps={{ style: { width: '100%' } }}
                      placeholderText={ContentId.All}
                      enableEmptySelection
                      onChange={({ newValues: newPaymentCurrencyCodes }) => {
                        if (
                          !isEqual(
                            tempQuery.paymentCurrencyCodes,
                            newPaymentCurrencyCodes
                          )
                        ) {
                          setTempQuery({
                            ...tempQuery,
                            paymentCurrencyCodes:
                              newPaymentCurrencyCodes?.length
                                ? newPaymentCurrencyCodes
                                : null,
                          });
                        }
                      }}
                    />
                  ),
                },
              ]
            : []),
          {
            filterId: 'paymentMethodIds' as FilterToolbarItemId,
            labelContentId: ContentId.PaymentMethodUsed,
            filterQueryKeys: ['paymentMethodIds'] as FilterToolbarItemId[],
            filterItem: (
              <PurchasePaymentMethodMultiSelector
                style={{ width: '100%' }}
                values={tempQuery.paymentMethodIds?.map(String) ?? []}
                enableEmptySelection
                operation={tempQuery.operations?.paymentMethodIdOp ?? null}
                operationOptionsContent={
                  supportMultiOperation
                    ? COLLECTION_FILTER_OP_TO_CID
                    : undefined
                }
                isHorizontalLayout={isHorizontalLayout}
                onChange={({ newValues, operation }) => {
                  if (
                    !isEqual(tempQuery.paymentMethodIds, newValues) ||
                    operation !== tempQuery.operations?.paymentMethodIdOp
                  ) {
                    setTempQuery({
                      ...tempQuery,
                      paymentMethodIds: newValues?.length
                        ? newValues.map(Number)
                        : null,
                      operations: supportMultiOperation
                        ? {
                            ...(tempQuery.operations as PurchaseQueryOperations),
                            paymentMethodIdOp: operation ?? null,
                          }
                        : null,
                    });
                  }
                }}
              />
            ),
          },
        ],
      },
      eventFilter,
      {
        titleContentId: ContentId.Delivery,
        type: 'row',
        items: [deliveryTypeFilter],
      },
      {
        titleContentId: ContentId.Status,
        type: 'row',
        items: [
          {
            filterId: 'purchaseOrderState' as FilterToolbarItemId,
            labelContentId: ContentId.ShowVoidCancelledPurchases,
            filterQueryKeys: ['purchaseOrderState'] as FilterToolbarItemId[],
            filterItem: (
              <Checkbox
                checked={
                  tempQuery.purchaseOrderState !== PurchaseOrderState.Active
                }
                onChange={(e) => {
                  const showCancelledAndVoided = e.target.checked;
                  const newPurchaseOrderState = showCancelledAndVoided
                    ? null
                    : PurchaseOrderState.Active;

                  if (newPurchaseOrderState !== tempQuery.purchaseOrderState) {
                    setTempQuery({
                      ...tempQuery,
                      purchaseOrderState: newPurchaseOrderState,
                    });
                  }
                }}
                labelPosition="right"
                label={
                  isHorizontalLayout ? undefined : (
                    <Content id={ContentId.ShowVoidCancelledPurchases} />
                  )
                }
              />
            ),
          },
        ],
      },
      {
        titleContentId: ContentId.Cost,
        items: [
          {
            filterId: 'totalCostHigh' as FilterToolbarItemId,
            labelContentId: ContentId.TotalCost,
            filterQueryKeys: [
              'totalCostLow',
              'totalCostHigh',
            ] as FilterToolbarItemId[],
            filterItem: (
              <Slider.Root
                min={0}
                max={50000}
                step={1}
                value={costHandle}
                renderHandle={(n) => (
                  <Slider.Handle>{formatNumber(n)}</Slider.Handle>
                )}
                onValueChange={([low, high]: [number, number]) => {
                  if (
                    low !== (tempQuery.totalCostLow || 0) ||
                    high !== tempQuery.totalCostHigh
                  ) {
                    const newQuery = {
                      ...tempQuery,
                      totalCostLow: low === 0 ? null : low,
                      totalCostHigh: high === 50000 ? null : high,
                    };
                    setTempQuery(newQuery);
                  }
                }}
              />
            ),
          },
          ...(hasFilterByCurrencyCodeFeature
            ? [
                {
                  filterId: 'currencyCode' as FilterToolbarItemId,
                  labelContentId: ContentId.Currency,
                  filterQueryKeys: ['currencyCode'] as FilterToolbarItemId[],
                  filterItem: (
                    <CurrencyFilterSelector
                      value={tempQuery.currencyCode ?? ''}
                      style={{ width: '100%' }}
                      placeholderText={ContentId.All}
                      onChange={(newCurrencyCode) => {
                        if (tempQuery.currencyCode !== newCurrencyCode) {
                          setTempQuery({
                            ...tempQuery,
                            currencyCode: newCurrencyCode?.length
                              ? newCurrencyCode
                              : null,
                          });
                        }
                      }}
                      enableEmptySelection
                    />
                  ),
                },
              ]
            : []),
          ...(hasFilterByCurrencyCodeMultiFeature
            ? [
                {
                  filterId: 'currencyCodes' as FilterToolbarItemId,
                  labelContentId: ContentId.Currency,
                  filterQueryKeys: ['currencyCodes'] as FilterToolbarItemId[],
                  filterItem: (
                    <CurrencyFilterMultiSelector
                      values={tempQuery.currencyCodes ?? []}
                      triggerProps={{ style: { width: '100%' } }}
                      placeholderText={ContentId.All}
                      onChange={({ newValues: newCurrencyCodes }) => {
                        if (
                          !isEqual(tempQuery.currencyCodes, newCurrencyCodes)
                        ) {
                          setTempQuery({
                            ...tempQuery,
                            currencyCodes: newCurrencyCodes?.length
                              ? newCurrencyCodes
                              : null,
                          });
                        }
                      }}
                      enableEmptySelection
                    />
                  ),
                },
              ]
            : []),
        ],
      },
      ...tagsFilter,
      ...(internalNotesFilter ? [internalNotesFilter] : []),
    ];
  }, [
    tempQuery,
    purchaseInfoFilters.items,
    showConsignmentFilter,
    isConsignmentFilter,
    hasDateFiltersRelativeExactCustomFeature,
    initialQuery.paymentDates,
    supportMultiOperation,
    isHorizontalLayout,
    hasFilterByPaymentCurrencyCodeMultiFeature,
    eventFilter,
    deliveryTypeFilter,
    hasFilterByCurrencyCodeFeature,
    hasFilterByCurrencyCodeMultiFeature,
    tagsFilter,
    internalNotesFilter,
    setTempQuery,
  ]);

  return { filters };
}
